import { useEffect, useRef } from "react";
import { hasTranslation, translateKey, useTranslation } from "../../i18n";
import { cn } from "../../lib/utils";
import { Badge, type BadgeTone } from "./badge";

export type StatusValue = string | number | null | undefined;

export function statusTone(value: StatusValue): BadgeTone {
  const status = String(value ?? "").toLowerCase();
  if (/^[1-5]\d{2}$/.test(status)) {
    const code = Number(status);
    if (code === 202) return "info";
    return code >= 400 ? "danger" : code >= 200 && code < 300 ? "success" : "info";
  }
  if (["healthy", "succeeded", "completed", "delivered", "active", "ready", "public", "published", "durable", "persisted", "verified", "live"].includes(status)) return "success";
  if (["failed", "delivery_failed", "error", "invalid", "rejected", "revoked", "banned"].includes(status)) return "danger";
  if (["degraded", "unavailable", "frozen", "warning", "temporary", "needs_correction", "needs route", "待接入渠道"].includes(status)) return "warning";
  if (["running", "first_response", "queued", "retrying", "pending", "accepted", "received", "submitted", "processing", "polling", "uploading", "storing", "cancelling"].includes(status)) return "info";
  return "neutral";
}

export function statusLabel(value: string): string {
  const key = value.toLowerCase();
  if (key === "待接入渠道") return translateKey("status.needs route");
  if (hasTranslation(`status.${key}`)) return translateKey(`status.${key}`);
  if (hasTranslation(`modality.${key}`)) return translateKey(`modality.${key}`);
  return value;
}

export function StatusBadge({ value, labelOverride, showDot = false, className, animateChanges = false }: Readonly<{
  value: StatusValue;
  labelOverride?: string;
  showDot?: boolean;
  className?: string;
  animateChanges?: boolean;
}>) {
  useTranslation();
  const badge = useRef<HTMLSpanElement | null>(null);
  const previous = useRef(value);
  useEffect(() => {
    const changed = previous.current !== value;
    previous.current = value;
    if (!animateChanges || !changed || !badge.current || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const animation = badge.current.animate([
      { opacity: 0.4, transform: "translateY(3px) scale(0.96)" },
      { opacity: 1, transform: "translateY(0) scale(1)" },
    ], { duration: 200, easing: "ease-out" });
    return () => animation.cancel();
  }, [animateChanges, value]);
  return <Badge
    ref={badge}
    tone={statusTone(value)}
    className={cn(animateChanges && "motion-safe:transition-colors motion-safe:duration-200", className)}
    icon={showDot ? <span aria-hidden="true" className="size-1.5 rounded-full bg-current" /> : undefined}
  >{labelOverride ?? statusLabel(String(value ?? "unknown"))}</Badge>;
}
